import { CheckCircle } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { InfoBox } from "@/components/ui/info-box";
import { Input } from "@/components/ui/input";
import { Modal } from "@/components/ui/modal";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { RegistryApiError, submitRegistryAppeal } from "@/lib/registry-api";
import type {
  BlockedDomain,
  CaseNotice,
  DomainNotice,
  SearchType,
} from "@/lib/types";

interface AppealModalProps {
  notice: CaseNotice | DomainNotice | BlockedDomain | null;
  searchType?: SearchType;
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  email: string;
  relationship: string;
  relationshipOther: string;
  reason: string;
}

const initialFormData: FormData = {
  name: "",
  email: "",
  relationship: "",
  relationshipOther: "",
  reason: "",
};

export function AppealModal({
  notice,
  searchType,
  isOpen,
  onClose,
}: AppealModalProps) {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (!notice) return null;

  const resetModal = () => {
    setFormData(initialFormData);
    setSubmitted(false);
    setIsSubmitting(false);
    setSubmitError(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await submitRegistryAppeal({
        referenceType: searchType ?? ("domain" in notice ? "domain" : "case"),
        reference: getReference(),
        name: formData.name,
        email: formData.email,
        relationship: formData.relationship,
        relationshipOther: formData.relationshipOther,
        reason: formData.reason,
      });
      setSubmitted(true);
    } catch (error) {
      setSubmitError(
        error instanceof RegistryApiError
          ? error.message
          : "Appeal submission failed."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const getReference = () => {
    if ("caseNumber" in notice && !("domain" in notice)) {
      return notice.caseNumber;
    }
    if ("domain" in notice) {
      return notice.domain;
    }
    return "";
  };

  const getCompanyName = () => {
    if ("companyName" in notice) {
      return notice.companyName;
    }
    return undefined;
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Appeal Decision">
      {!submitted ? (
        <form
          onSubmit={(event) => {
            void handleSubmit(event);
          }}
          className="space-y-4 sm:space-y-6"
        >
          <InfoBox variant="gray">
            <p className="text-sm break-words">
              <strong>Reference:</strong> {getReference()}
            </p>
            {getCompanyName() && (
              <p className="text-sm mt-1">
                <strong>Company:</strong> {getCompanyName()}
              </p>
            )}
          </InfoBox>

          <Input
            id="name"
            name="name"
            type="text"
            label="Full Name *"
            required
            value={formData.name}
            onChange={handleChange}
            disabled={isSubmitting}
          />

          <Input
            id="email"
            name="email"
            type="email"
            label="Email Address *"
            required
            value={formData.email}
            onChange={handleChange}
            disabled={isSubmitting}
          />

          <div>
            <Select
              id="relationship"
              name="relationship"
              label="Relationship to Content *"
              required
              value={formData.relationship}
              onChange={handleChange}
              disabled={isSubmitting}
            >
              <option value="">Select...</option>
              <option value="individual">Individual depicted</option>
              <option value="representative">Legal representative</option>
              <option value="user">Internet user</option>
              <option value="owner">Domain owner</option>
              <option value="hosting">Hosting Provider</option>
              <option value="isp">ISP</option>
              <option value="other">Other</option>
            </Select>
            {formData.relationship === "other" && (
              <Input
                id="relationshipOther"
                name="relationshipOther"
                type="text"
                required
                value={formData.relationshipOther}
                onChange={handleChange}
                disabled={isSubmitting}
                placeholder="Please specify"
                className="mt-2"
              />
            )}
          </div>

          <Textarea
            id="reason"
            name="reason"
            label="Reason for Appeal *"
            required
            value={formData.reason}
            onChange={handleChange}
            disabled={isSubmitting}
            rows={6}
            placeholder="Please provide detailed information about why you believe this decision should be reconsidered..."
          />

          {submitError && (
            <InfoBox variant="error">
              <p className="text-sm">{submitError}</p>
            </InfoBox>
          )}

          <InfoBox variant="warning">
            <p className="text-sm">
              <strong>Note:</strong> Appeals are reviewed within 5-7 business
              days. You will receive a response at the email address provided.
            </p>
          </InfoBox>

          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              {isSubmitting ? "Submitting..." : "Submit Appeal"}
            </Button>
          </div>
        </form>
      ) : (
        <div className="text-center py-8">
          <CheckCircle size={64} className="mx-auto text-green-600 mb-4" />
          <h3 className="text-green-900 mb-2 text-base sm:text-lg font-medium">
            Appeal Submitted Successfully
          </h3>
          <p className="text-gray-700 mb-6 text-sm sm:text-base">
            Your appeal has been received and will be reviewed within 5-7
            business days. You will receive a response at{" "}
            <strong className="break-words">{formData.email}</strong>.
          </p>
          <p className="text-sm text-gray-600 mb-6 break-words">
            Reference: {getReference()}
          </p>
          <Button onClick={handleClose} className="w-full sm:w-auto">
            Close
          </Button>
        </div>
      )}
    </Modal>
  );
}
